/**
 * Financials domain types.
 *
 * Centers on Accounts Receivable: invoices → payments → aging → statements.
 * AP (bills) is modeled here at a light level for completeness but full
 * AP workflow is a phase-2 concern — for now, focus on AR.
 *
 * Invoice lifecycle:
 *   draft → sent → partial → paid        (happy path)
 *                       ↘  past_due      (not paid by due date)
 *                       ↘  void          (cancelled after send)
 *
 * An invoice can be linked to an Order (most common) or free-standing
 * (deposit invoices, service invoices, etc.).
 *
 * Payments are applied against one or more invoices; each application
 * reduces the invoice's balanceDue. When balanceDue hits zero, the
 * invoice is auto-marked 'paid'.
 */

// ─────────── Status enums ───────────

export type InvoiceStatus = 'draft' | 'sent' | 'partial' | 'paid' | 'past_due' | 'void';

export const INVOICE_STATUSES: InvoiceStatus[] = [
  'draft', 'sent', 'partial', 'paid', 'past_due', 'void',
];

export const INVOICE_STATUS_LABELS: Record<InvoiceStatus, string> = {
  draft:    'Draft',
  sent:     'Sent',
  partial:  'Partially Paid',
  paid:     'Paid',
  past_due: 'Past Due',
  void:     'Void',
};

export type PaymentMethod =
  | 'check'
  | 'ach'
  | 'wire'
  | 'credit_card'
  | 'cash'
  | 'other';

export const PAYMENT_METHOD_LABELS: Record<PaymentMethod, string> = {
  check:       'Check',
  ach:         'ACH',
  wire:        'Wire Transfer',
  credit_card: 'Credit Card',
  cash:        'Cash',
  other:       'Other',
};

export type PaymentTerms = 'due_on_receipt' | 'net_15' | 'net_30' | 'net_45' | 'net_60' | 'custom';

export const PAYMENT_TERMS_LABELS: Record<PaymentTerms, string> = {
  due_on_receipt: 'Due on Receipt',
  net_15:         'Net 15',
  net_30:         'Net 30',
  net_45:         'Net 45',
  net_60:         'Net 60',
  custom:         'Custom',
};

export function daysFromTerms(terms: PaymentTerms): number {
  switch (terms) {
    case 'due_on_receipt': return 0;
    case 'net_15': return 15;
    case 'net_30': return 30;
    case 'net_45': return 45;
    case 'net_60': return 60;
    case 'custom': return 30;
  }
}

// ─────────── Invoice ───────────

export type InvoiceLineItem = {
  id: string;
  description: string;
  quantity: number;
  unit?: string;
  unitPrice: number;
  taxable?: boolean;
  sku?: string;
};

export type InvoiceTotals = {
  subtotal: number;
  discount: number;
  taxable: number;
  taxRate: number;
  tax: number;
  shipping: number;
  total: number;
  amountPaid: number;       // sum of applied payments
  balanceDue: number;       // total − amountPaid
};

export type Invoice = {
  id: string;
  number: string;             // 'INV-2601'
  status: InvoiceStatus;

  clientId: string;
  clientName?: string;
  contactId?: string;

  orderId?: string;            // optional link to a work order
  orderNumber?: string;

  issueDate: string;
  dueDate: string;
  terms: PaymentTerms;
  customTermsDays?: number;

  // Billing snapshot (so the invoice remains accurate even if client info changes)
  billTo?: {
    name?: string;
    attention?: string;
    street1?: string; street2?: string;
    city?: string; state?: string; postalCode?: string; country?: string;
    email?: string;
  };

  lineItems: InvoiceLineItem[];

  discountPct?: number;
  taxRatePct?: number;
  shipping?: number;
  notes?: string;
  internalNotes?: string;
  termsAndConditions?: string;

  totals: InvoiceTotals;

  sentAt?: string;
  voidedAt?: string;
  voidReason?: string;
  lastReminderAt?: string;

  createdAt?: string;
  createdBy?: string;
  updatedAt?: string;
  updatedBy?: string;
};

// ─────────── Payment ───────────

export type PaymentApplication = {
  invoiceId: string;
  invoiceNumber?: string;
  amount: number;
};

export type Payment = {
  id: string;
  number: string;              // 'PMT-2601'
  clientId: string;
  clientName?: string;

  amount: number;              // total payment received
  unapplied: number;           // amount − sum(applications)
  method: PaymentMethod;
  reference?: string;          // check # / wire confirmation / etc.
  receivedDate: string;

  applications: PaymentApplication[];

  notes?: string;

  createdAt?: string;
  createdBy?: string;
};

// ─────────── Aging ───────────

export type AgingBucket = 'current' | '1-30' | '31-60' | '61-90' | '90+';

export const AGING_BUCKET_LABELS: Record<AgingBucket, string> = {
  current: 'Current',
  '1-30':  '1–30 days',
  '31-60': '31–60 days',
  '61-90': '61–90 days',
  '90+':   '90+ days',
};

export function bucketFor(dueDate: string): AgingBucket {
  const days = Math.floor((Date.now() - new Date(dueDate).getTime()) / (24 * 60 * 60 * 1000));
  if (days <= 0) return 'current';
  if (days <= 30) return '1-30';
  if (days <= 60) return '31-60';
  if (days <= 90) return '61-90';
  return '90+';
}

// ─────────── Utilities ───────────

export function computeInvoiceTotals(
  lineItems: InvoiceLineItem[],
  opts: { discountPct?: number; taxRatePct?: number; shipping?: number; amountPaid?: number } = {},
): InvoiceTotals {
  const subtotal = lineItems.reduce((s, li) => s + li.quantity * li.unitPrice, 0);
  const discountPct = opts.discountPct ?? 0;
  const discount = subtotal * discountPct;
  const taxable = lineItems
    .filter((li) => li.taxable !== false)
    .reduce((s, li) => s + li.quantity * li.unitPrice, 0) * (1 - discountPct);
  const taxRate = opts.taxRatePct ?? 0;
  const tax = taxable * taxRate;
  const shipping = opts.shipping ?? 0;
  const total = subtotal - discount + tax + shipping;
  const amountPaid = opts.amountPaid ?? 0;
  return {
    subtotal, discount, taxable, taxRate, tax, shipping,
    total, amountPaid, balanceDue: Math.max(0, total - amountPaid),
  };
}
